import * as Sentry from "@sentry/browser";
import { z } from "zod";

import { setWithExpiry, getWithExpiry } from "app/utils/sessionStorage";
import { validateSchema } from "app/utils/validateSchema";

import type { Headers } from "react-relay-network-modern/lib";

export const SamlProviderSchema = z.object({
  name: z.string(),
  idp_id: z.string(),
  display_name: z.string().optional(),
});

export const SamlProvidersArraySchema = z.array(SamlProviderSchema);

export type SamlProvider = z.infer<typeof SamlProviderSchema>;

export interface Configs {
  cognito: {
    cognito_install: string;
    cognito_user_pool_region: string;
    cognito_user_pool_id: string;
    cognito_user_pool_client_id: string;
    saml: Record<string, string>;
    saml_providers?: SamlProvider[];
  };

  cubejs: {
    cubejs_domain: string;
  };

  deployment: {
    gql_uri: string;
    headers?: Headers;
    // Local-dev only (`just local-console`): absolute URL of the local IdP's
    // token endpoint, which the console hits directly to mint a JWT.
    local_idp_token_url?: string;
  };

  docs?: {
    docs_url: string;
  };

  jun0?: {
    jun0_url: string;
  };

  redash: {
    redash_uri: string;
  };

  sinistral?: {
    sinistral_url: string;
  };

  superset?: {
    superset_uri: string;
  };
}

const CONFIG_TTL_MINUTES = 60;

// A response that says not to keep it still gets a few seconds. That is short
// enough that the next use of a config re-checks the file, and long enough that
// the several reads of one page load do not each fetch it again.
const UNKEEPABLE_TTL_MINUTES = 5 / 60;

const ConfigsSchema = z.object({
  cognito: z.object({
    cognito_install: z.string(),
    cognito_user_pool_region: z.string(),
    cognito_user_pool_id: z.string(),
    cognito_user_pool_client_id: z.string(),
    saml: z.record(z.string()),
    saml_providers: SamlProvidersArraySchema.optional(),
  }),
  cubejs: z.object({ cubejs_domain: z.string() }),
  deployment: z.object({
    gql_uri: z.string(),
    headers: z.record(z.string()).optional(),
    local_idp_token_url: z.string().optional(),
  }),
  docs: z.object({ docs_url: z.string() }).optional(),
  jun0: z.object({ jun0_url: z.string() }).optional(),
  redash: z.object({ redash_uri: z.string() }),
  sinistral: z.object({ sinistral_url: z.string() }).optional(),
  superset: z.object({ superset_uri: z.string() }).optional(),
}) satisfies z.ZodType<Configs>;

/**
 * Fetches one config file, and reports how long its response may be kept.
 *
 * A 404 is an answer, not a failure: the server states that the file does not
 * exist, so this returns `undefined` for the value. A body of `null` reads the
 * same way, which keeps a fresh read and a cached read in agreement. Any other
 * error response throws, because the server did not state anything about the
 * file.
 */
async function _fetchConfig<T>(
  name: string,
): Promise<{ value: T | undefined; ttlMinutes: number }> {
  const response = await fetch(`/config/${name}.json`);
  if (!response.ok && response.status !== 404) {
    throw new Error(`Unable to fetch /config/${name}.json: ${response.status}`);
  }
  return {
    value: response.ok ? ((await response.json()) ?? undefined) : undefined,
    ttlMinutes: _ttlMinutesFor(response.headers.get("cache-control")),
  };
}

/**
 * Reads a `Cache-Control` header, and reports how long to keep the response.
 *
 * The server decides. A response that says not to keep it gets seconds instead
 * of an hour, whether it carries a file or a 404: the server knows whether the
 * file is settled, and the console does not second-guess it.
 *
 * Directive names are matched without case, because a server may send any case.
 * `max-age` is read only far enough to tell a zero from every other value.
 */
function _ttlMinutesFor(cacheControl: string | null): number {
  const directives = (cacheControl ?? "")
    .toLowerCase()
    .split(",")
    .map((directive) => directive.trim());
  const isUnkeepable = directives.some(
    (directive) =>
      directive === "no-cache" ||
      directive === "no-store" ||
      /^max-age\s*=\s*0+$/.test(directive),
  );
  return isUnkeepable ? UNKEEPABLE_TTL_MINUTES : CONFIG_TTL_MINUTES;
}

/**
 * Reads one config file, from the session cache when it holds a fresh copy.
 *
 * Each file is cached under its own key, so one short-lived file does not hold
 * back the rest, and the response decides how long its entry lives. A response
 * that `_fetchConfig` rejects never enters the cache.
 *
 * `cacheAbsence` states whether an absent file is a valid answer to keep. For a
 * file that every environment must have, a 404 means a broken deployment, and a
 * cached 404 would keep the console broken for the whole TTL.
 *
 * The cache is an optimization, so neither a read nor a write of it may change
 * the result. Session storage rejects both when it is full, and when the browser
 * blocks storage.
 */
async function _readConfig<T>(
  name: string,
  { cacheAbsence }: { cacheAbsence: boolean },
): Promise<T | undefined> {
  const key = `config:${name}`;
  try {
    const cached = getWithExpiry(key);
    if (cached !== null) {
      return JSON.parse(cached) ?? undefined;
    }
  } catch (e) {
    console.warn(`Unable to read the cached /config/${name}.json`, e);
  }

  const { value, ttlMinutes } = await _fetchConfig<T>(name);
  if (value !== undefined || cacheAbsence) {
    try {
      setWithExpiry(key, JSON.stringify(value ?? null), ttlMinutes);
    } catch (e) {
      console.warn(`Unable to cache /config/${name}.json`, e);
    }
  }
  return value;
}

/** Reads a config file that every environment must have. */
async function _getConfig<T>(name: string): Promise<T> {
  const value = await _readConfig<T>(name, { cacheAbsence: false });
  if (value === undefined) {
    throw new Error(`Missing required config /config/${name}.json`);
  }
  return value;
}

/**
 * Reads a config file that may be absent in some environments.
 *
 * A failure is not worth a report: the file is optional, and the server logs
 * its own errors.
 */
async function _getOptionalConfig<T>(name: string): Promise<T | undefined> {
  try {
    return await _readConfig<T>(name, { cacheAbsence: true });
  } catch {
    return undefined;
  }
}

async function _loadConfigs(): Promise<Configs> {
  const [cognito, cubejs, deployment, docs, jun0, redash, sinistral, superset] =
    await Promise.all([
      _getConfig<Configs["cognito"]>("cognito"),
      _getConfig<Configs["cubejs"]>("cubejs"),
      _getConfig<Configs["deployment"]>("deployment"),
      // Every consumer of the docs URL already handles its absence.
      _getOptionalConfig<NonNullable<Configs["docs"]>>("docs"),
      // Jun0 may not have config yet, which is ok.
      _getOptionalConfig<NonNullable<Configs["jun0"]>>("jun0"),
      _getConfig<Configs["redash"]>("redash"),
      // Sinistral may not be deployed in this environment, which is ok.
      _getOptionalConfig<NonNullable<Configs["sinistral"]>>("sinistral"),
      // Superset may not be deployed in this environment, which is ok.
      _getOptionalConfig<NonNullable<Configs["superset"]>>("superset"),
    ]);
  const configs: Configs = {
    cognito,
    cubejs,
    deployment,
    docs,
    jun0,
    redash,
    sinistral,
    superset,
  };
  // Warn only: a slightly malformed config should not take down the console. A
  // malformed file is a deployment fault, which the server that serves it logs.
  const [, validationErrors] = validateSchema(ConfigsSchema, configs);
  if (validationErrors) {
    console.warn("Fetched configs failed validation:", validationErrors);
  }
  return configs;
}

let inflightConfigs: Promise<Configs> | null = null;

const configsListeners = new Set<(configs: Configs) => void>();

/**
 * Registers a callback for every successful read of the configs, and returns a
 * function that removes it again.
 *
 * An optional file that failed one read may succeed on the next one. A caller
 * that copies the configs elsewhere, such as the Relay store, needs to hear
 * about that recovery to stay in step.
 */
export function onConfigsLoaded(
  listener: (configs: Configs) => void,
): () => void {
  configsListeners.add(listener);
  return () => configsListeners.delete(listener);
}

/**
 * Reads the config files for this deployment.
 *
 * Concurrent callers share one read, and the promise is dropped once it settles.
 * The session cache is the only cache that outlives a call, so a file that the
 * server says not to keep is read again on the next call, and a file that failed
 * gets another chance. Sequential callers do not share a read: `RelayEnvironment`
 * shares one per request itself, because its two middlewares run in turn.
 */
export function getConfigs(): Promise<Configs> {
  inflightConfigs ??= _loadConfigs()
    .then((configs) => {
      configsListeners.forEach((listener) => {
        // A listener must never fail the read that told it the news. A listener
        // that throws is a fault in the console itself, so it gets reported.
        try {
          listener(configs);
        } catch (e) {
          console.warn("A config listener failed", e);
          Sentry.captureException(e);
        }
      });
      return configs;
    })
    .finally(() => {
      inflightConfigs = null;
    });
  return inflightConfigs;
}
